<script>
import { mapGetters, mapActions } from 'vuex'
import { TheMask } from 'vue-the-mask'

import { apiSecond } from '@/boot/axios'
import { loadFile } from '@/helpers'
import Fish from '@/components/components/Inputs/fish'
import Region from '@/components/components/Inputs/region'
import AqEmpty from '@/components/aqua/AqEmpty.vue'
import AqField from '@/components/aqua/AqField.vue'
import AqKpi from '@/components/aqua/AqKpi.vue'
import AqPageHeader from '@/components/aqua/AqPageHeader.vue'
import AqStatus from '@/components/aqua/AqStatus.vue'
import AquaSelect from '@/views/account/reporting/aqua/AquaSelect.vue'

const emptyFish = () => ({
	id: new Date().getTime() + Math.random(),
	fish_id: null,
	design_capacity: 0,
})

export default {
	name: 'ZpmCreatePage',
	components: {
		AqEmpty,
		TheMask,
		Fish,
		Region,
		AqField,
		AqKpi,
		AqPageHeader,
		AqStatus,
		AquaSelect,
	},
	data() {
		return {
			bin: null,
			title: null,
			code: null,
			address: null,
			registration_date: null,
			activity_type: null,
			activity_type_id: null,
			region_id: null,
			region_title: null,
			activityTypes: [],
			status: 'active',
			status_reason: null,
			status_changed_at: null,
			archived_at: null,
			history: [],
			statusTarget: null,
			statusReason: '',
			archiveComment: '',
			start_date: null,
			end_date: null,
			fishes: [emptyFish()],
			// read-only поля, заполняются из карточки
			zpm_nikad: null,
			transferred_at: null,
			user_nikad_agro_culture_id: null,
			total_capacity: null,
			total_remainder: null,
			loaded: false,
			notFound: false,
			busy: false,
			errorMessage: {
				status: null,
				messages: null,
				text: null,
			},
			successText: null,
			sourcePage: null,
		}
	},
	computed: {
		...mapGetters(['_getFishes']),
		isNotEditable() {
			return !!this.$route.meta.isView
		},
		isEdit() {
			return !!this.$route.meta.isEdit
		},
		isSubject() {
			return !!this.$route.meta.subject
		},
		regionalConfines() {
			const user = this.$store.state.userObject
			if (!['tir', 'mio'].includes(user?.role?.name)) return []
			return (user.regions || []).map((region) => ({ id: region.region_id }))
		},
		isTransferred() {
			return !!this.transferred_at
		},
		canManage() {
			const hasPermission = this.$store.getters.userPermission
			return !this.isSubject && !!hasPermission && !!hasPermission(['change_zpm'])
		},
		selectedActivityType: {
			get() {
				return this.activityTypes.find((type) => type.id === this.activity_type_id) || null
			},
			set(type) {
				this.activity_type_id = type ? type.id : null
			},
		},
		canEditRegistry() {
			return this.isNotEditable && this.canManage && !this.archived_at
		},
		statusActions() {
			return ['active', 'suspended', 'terminated'].filter((value) => value !== this.status)
		},
		statusOptions() {
			return [
				{ value: 'active', label: this.$t('zpm.status_active') },
				{ value: 'inactive', label: this.$t('zpm.status_inactive') },
			]
		},
		selectedStatus: {
			get() {
				return this.statusOptions.find((o) => o.value === this.status) || this.statusOptions[0]
			},
			set(option) {
				this.status = option?.value || 'active'
			},
		},
		getTitle() {
			if (this.isNotEditable) return this.$t('titles.zpm_viewing')
			if (this.isEdit) return this.$t('zpm.edit_title')
			return this.$t('titles.zpm_addings')
		},
		listPath() {
			return '/' + this.$i18n.locale + '/account/' + (this.isSubject ? 'zpm-my/' : 'zpm/')
		},
		pageQuery() {
			return this.sourcePage ? { page: this.sourcePage } : {}
		},
		headerTitle() {
			return this.isNotEditable && this.title ? this.title : this.getTitle
		},
		activitySelectOptions() {
			return [
				{ value: null, text: this.$t('placeholder.choose') },
				...this.activityTypes.map((type) => ({ value: type.id, text: this.activityTypeLabel(type) })),
			]
		},
		statusSelectOptions() {
			return this.statusOptions.map((option) => ({ value: option.value, text: option.label }))
		},
		activityTypeTitle() {
			return this.selectedActivityType ? this.activityTypeLabel(this.selectedActivityType) : '—'
		},
		fishTitles() {
			return Object.fromEntries((this._getFishes || []).map((fish) => [fish.id, fish.title]))
		},
		remainderPercent() {
			return this.percentOf(this.total_remainder, this.total_capacity)
		},
	},
	watch: {
		// просмотр ↔ редактирование рендерят один и тот же компонент — перечитываем карточку
		'$route.path'() {
			this.clearError()
			if (!this.$route.params.id) {
				Object.assign(this.$data, this.$options.data.call(this), { loaded: true })
				return
			}
			this.fetchZpm()
		},
	},
	async mounted() {
		this.sourcePage = this.$route.query.page || null
		this._fetchFishes()
		this.loadActivityTypes()
		await this.fetchZpm()
	},
	beforeCreate() {
		if (!localStorage.token) {
			this.$router.push('/ru/login')
		}
	},
	methods: {
		...mapActions(['_fetchFishes']),
		fieldErrors(field) {
			return this.errorMessage.messages?.[field] || null
		},
		statusLabel(status) {
			if (!status) return '-'
			const key = 'zpm.status_' + status
			return this.$te(key) ? this.$t(key) : status
		},
		formatDateTime(value) {
			if (!value) return '-'
			const date = new Date(value)
			if (Number.isNaN(date.getTime())) return value
			return date.toLocaleString('ru-RU', {
				day: '2-digit',
				month: '2-digit',
				year: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			})
		},
		showError(text) {
			this.errorMessage.text = text
			this.$modal.show('modal_error')
		},
		catchError(error) {
			const status = error?.response?.status
			const data = error?.response?.data || {}
			this.errorMessage.status = status || null

			if (status == 401) {
				this.$router.push('/' + this.$i18n.locale + '/login')
				return
			}
			if (status == 422) {
				// валидация: { message, errors: { поле: [...] } }; бизнес-отказ: { success: false, message }
				const errors = data.errors || null
				this.errorMessage.messages = errors
				if (errors) {
					const texts = []
					Object.values(errors).forEach((msgs) => [].concat(msgs).forEach((msg) => texts.push(msg)))
					this.showError(texts.join('. '))
				} else {
					this.showError(data.message || data.error_message)
				}
				return
			}
			if (status == 500) {
				this.showError(this.$t('system_message.500'))
				return
			}
			this.showError(data.message || data.error_message || this.$t('zpm.error'))
		},
		applyRecord(record) {
			this.bin = record.bin
			this.title = record.title
			this.code = record.code
			this.address = record.address
			this.registration_date = record.registration_date
				? String(record.registration_date).slice(0, 10)
				: null
			this.activity_type = record.activity_type
			this.activity_type_id = record.activity_type_id || null
			this.region_id = record.region_id || null
			this.region_title = record.region_title || null
			this.status = record.status || 'active'
			this.status_reason = record.status_reason || null
			this.status_changed_at = record.status_changed_at || null
			this.archived_at = record.archived_at || null
			this.history = record.history || []
			this.start_date = record.start_date ? String(record.start_date).slice(0, 10) : null
			this.end_date = record.end_date ? String(record.end_date).slice(0, 10) : null
			this.zpm_nikad = record.zpm_nikad
			this.transferred_at = record.transferred_at
			this.user_nikad_agro_culture_id = record.user_nikad_agro_culture_id
			this.total_capacity = record.total_capacity
			this.total_remainder = record.total_remainder
			this.fishes = (record.fishes || []).map((fish) => ({
				id: fish.id,
				fish_id: fish.fish_id,
				design_capacity: fish.design_capacity,
				remainder: fish.remainder,
				fish_name: fish.fish_name,
			}))
			if (!this.fishes.length && !this.isNotEditable) this.fishes = [emptyFish()]
		},
		async fetchZpm() {
			if (!this.$route.params.id) {
				this.loaded = true
				return
			}
			try {
				const response = await apiSecond.get(`v1/zpm/${this.$route.params.id}`)
				const record = response.data?.data
				if (record) {
					this.applyRecord(record)
					if (this.isEdit && this.isTransferred) {
						this.$router.replace({
							path: '/' + this.$i18n.locale + '/account/zpm/' + record.id,
							query: this.pageQuery,
						})
					}
				}
			} catch (error) {
				if ([403, 404].includes(error?.response?.status)) this.notFound = true
				else this.catchError(error)
			} finally {
				this.loaded = true
			}
		},
		addFishes() {
			this.fishes.push(emptyFish())
		},
		removeFish(index) {
			this.fishes.splice(index, 1)
		},
		validate(bin) {
			if (!/^\d{12}$/.test(bin)) {
				this.errorMessage.messages = { bin: [this.$t('zpm.bin_invalid')] }
				this.showError(this.$t('zpm.bin_invalid'))
				return false
			}
			if (!this.title || !String(this.title).trim()) {
				this.errorMessage.messages = { title: [this.$t('zpm.title_required')] }
				this.showError(this.$t('zpm.title_required'))
				return false
			}
			if (!this.start_date || !this.end_date) {
				this.showError(this.$t('zpm.dates_required'))
				return false
			}
			if (this.end_date < this.start_date) {
				this.showError(this.$t('zpm.end_before_start'))
				return false
			}
			if (
				!this.fishes.length ||
				this.fishes.some((f) => !f.fish_id || !(Number(f.design_capacity) > 0))
			) {
				this.showError(this.$t('zpm.fishes_required'))
				return false
			}
			const ids = this.fishes.map((f) => f.fish_id)
			if (new Set(ids).size !== ids.length) {
				this.showError(this.$t('zpm.fishes_duplicate'))
				return false
			}
			return true
		},
		async onSubmit() {
			if (this.isNotEditable || this.busy) return
			this.clearError()

			const bin = String(this.bin || '').replace(/\s/g, '')
			if (!this.validate(bin)) return

			const sendData = {
				bin,
				title: String(this.title).trim(),
				code: this.code || null,
				address: this.address || null,
				registration_date: this.registration_date || null,
				activity_type: this.activity_type || null,
				activity_type_id: this.activity_type_id || null,
				region_id: this.region_id || null,
				status: this.status || 'active',
				start_date: this.start_date,
				end_date: this.end_date,
				fishes: this.fishes.map((fish) => ({
					fish_id: fish.fish_id,
					design_capacity: Number(fish.design_capacity),
				})),
			}

			if (this.isEdit) delete sendData.status

			try {
				this.busy = true
				const response = this.isEdit
					? // nginx пропускает в CORS только GET/POST — PUT шлём через method spoofing Laravel
					  await apiSecond.post(`v1/zpm/${this.$route.params.id}`, { ...sendData, _method: 'PUT' })
					: await apiSecond.post('v1/zpm/store', sendData)

				const id = response.data?.data?.id || this.$route.params.id
				this.$toast.success(this.$t(this.isEdit ? 'toast.saved' : 'toast.created'))
				// после сохранения — в карточку: там проверка, PDF и перенос в склад
				this.$router.push({
					path: '/' + this.$i18n.locale + '/account/zpm/' + id,
					query: this.pageQuery,
				})
			} catch (error) {
				this.catchError(error)
			} finally {
				this.busy = false
			}
		},
		async transferZpm() {
			if (this.busy) return
			try {
				this.busy = true
				const response = await apiSecond.post(`v1/zpm/${this.$route.params.id}/transfer`)
				this.$modal.hide('modal_transfer')
				if (response.data?.data) this.applyRecord(response.data.data)
				this.successText = response.data?.message || this.$t('zpm.transfer_success')
				this.$modal.show('modal_success')
			} catch (error) {
				this.$modal.hide('modal_transfer')
				this.catchError(error)
			} finally {
				this.busy = false
			}
		},
		async deleteZpm() {
			if (this.busy) return
			try {
				this.busy = true
				await apiSecond.post(`v1/zpm/${this.$route.params.id}`, {
					_method: 'DELETE',
					comment: this.archiveComment.trim() || null,
				})
				this.$modal.hide('modal_delete')
				this.archiveComment = ''
				this.$toast.success(this.$t('zpm.archived_success'))
				this.$router.push({ path: this.listPath, query: this.pageQuery })
			} catch (error) {
				this.$modal.hide('modal_delete')
				this.catchError(error)
			} finally {
				this.busy = false
			}
		},
		async saveRegistry() {
			if (this.busy) return
			try {
				this.busy = true
				await apiSecond.post(`v1/zpm/${this.$route.params.id}`, {
					_method: 'PUT',
					activity_type_id: this.activity_type_id || null,
					region_id: this.region_id || null,
				})
				this.$toast.success(this.$t('toast.saved'))
				await this.fetchZpm()
			} catch (error) {
				this.catchError(error)
			} finally {
				this.busy = false
			}
		},
		async restoreZpm() {
			if (this.busy) return
			try {
				this.busy = true
				await apiSecond.post(`v1/zpm/${this.$route.params.id}/restore`)
				this.$toast.success(this.$t('zpm.restored_success'))
				await this.fetchZpm()
			} catch (error) {
				this.catchError(error)
			} finally {
				this.busy = false
			}
		},
		openStatusModal(target) {
			this.statusTarget = target
			this.statusReason = ''
			this.$modal.show('modal_status')
		},
		async changeStatus() {
			if (this.busy) return
			if (this.statusTarget !== 'active' && this.statusReason.trim().length < 5) {
				this.showError(this.$t('zpm.status_reason_required'))
				return
			}
			try {
				this.busy = true
				await apiSecond.post(`v1/zpm/${this.$route.params.id}/status`, {
					status: this.statusTarget,
					reason: this.statusReason.trim() || null,
				})
				this.$modal.hide('modal_status')
				this.$toast.success(this.$t('zpm.status_changed'))
				await this.fetchZpm()
			} catch (error) {
				this.$modal.hide('modal_status')
				this.catchError(error)
			} finally {
				this.busy = false
			}
		},
		async loadActivityTypes() {
			try {
				const { data } = await apiSecond.get('v1/zpm/activity-types')
				this.activityTypes = data.data || []
			} catch (error) {
				this.catchError(error)
			}
		},
		activityTypeLabel(type) {
			return this.$i18n.locale === 'kz' && type.title_kz ? type.title_kz : type.title
		},
		historyChanges(item) {
			if (!item.changes) return ''
			const status = (value) => (value ? this.statusLabel(value) : value)
			return Object.keys(item.changes)
				.filter((field) => Array.isArray(item.changes[field]) || item.changes[field] !== null)
				.map((field) => {
					const label = this.$te(`zpm.history_fields.${field}`) ? this.$t(`zpm.history_fields.${field}`) : field
					const value = item.changes[field]
					if (!Array.isArray(value)) return `${label}: ${field === 'status' ? status(value) : value}`
					const [before, after] = field === 'status' ? value.map(status) : value
					return `${label}: ${before ?? '—'} → ${after ?? '—'}`
				})
				.join('; ')
		},
		async downloadPdf() {
			if (this.busy) return
			try {
				this.busy = true
				const response = await apiSecond.get(`v1/zpm/${this.$route.params.id}/pdf`, {
					responseType: 'blob',
				})
				loadFile(
					response.data,
					'application/pdf',
					'ZPM_' + (this.zpm_nikad || this.$route.params.id),
				)
			} catch (error) {
				// тело ошибки пришло blob'ом — разбираем JSON, чтобы показать текст
				const blob = error?.response?.data
				if (blob instanceof Blob) {
					try {
						error.response.data = JSON.parse(await blob.text())
					} catch (e) {
						error.response.data = {}
					}
				}
				this.catchError(error)
			} finally {
				this.busy = false
			}
		},
		percentOf(value, total) {
			const base = Number(total)
			if (!base) return 0
			return Math.round((Number(value || 0) / base) * 100)
		},
		formatNum(value) {
			if (value === null || value === undefined || value === '') return '—'
			const number = Number(value)
			return Number.isNaN(number) ? value : number.toLocaleString('ru-RU', { maximumFractionDigits: 3 })
		},
		formatDay(value) {
			if (!value) return '—'
			const parts = String(value).slice(0, 10).split('-')
			return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : value
		},
		setActivityType(value) {
			this.activity_type_id = value || null
		},
		setStatus(value) {
			this.status = value || 'active'
		},
		fishName(fish) {
			return fish.fish_name || this.fishTitles[fish.fish_id] || '—'
		},
		historyTone(action) {
			const tones = { created: 'success', restored: 'success', transferred: 'info', status: 'warning' }
			return tones[action] || 'neutral'
		},
		clearError() {
			this.errorMessage = { status: null, messages: null, text: null }
		},
	},
}
</script>

<template>
	<div
		class="wrap__content aq-page"
		v-bind:class="{
			'wrap__content--preloader': !_getFishes || !loaded,
		}"
	>
		<v-preloader v-if="!_getFishes || !loaded" :message="errorMessage"></v-preloader>

		<div v-else class="container">
			<div class="row">
				<div class="col-xl-2 col-lg-3">
					<v-sidebar :active="[isSubject ? 'ZpmMy' : 'Zpm']"></v-sidebar>
				</div>
				<div class="col-xl-10 col-lg-9">
					<div class="content-wrapper">
						<div class="card__content aq-shell">
							<AqPageHeader
								:title="headerTitle"
								:eyebrow="$t('aqua.ui.zpm.eyebrow')"
								:back="{ path: listPath, query: pageQuery }"
								:back-label="isSubject ? $t('aqua.ui.back') : $t('aqua.ui.zpm.to_list')"
							>
								<template v-if="$route.params.id && !notFound" #meta>
									<span v-if="zpm_nikad" class="aq-code">{{ zpm_nikad }}</span>
									<AqStatus :value="status" :label="statusLabel(status)" />
									<AqStatus v-if="archived_at" tone="neutral" :label="$t('zpm.archived')" />
									<AqStatus
										v-if="isNotEditable && !isSubject"
										:tone="isTransferred ? 'success' : 'neutral'"
										:label="isTransferred ? $t('zpm.transferred_yes') : $t('zpm.not_transferred')"
									/>
								</template>
								<template v-if="isNotEditable && !notFound" #actions>
									<button type="button" class="aq-btn" :disabled="busy" @click="downloadPdf">
										<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
											<path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
										</svg>
										{{ $t('zpm.pdf') }}
									</button>
									<router-link
										v-if="canManage && !isTransferred && !archived_at"
										:to="{
											path: '/' + $i18n.locale + '/account/zpm/' + $route.params.id + '/edit',
											query: pageQuery,
										}"
										class="aq-btn aq-btn--primary"
									>
										<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
											<path d="M4 20h4L19 9l-4-4L4 16z" />
										</svg>
										{{ $t('button.edit') }}
									</router-link>
								</template>
							</AqPageHeader>

							<div v-if="notFound" class="aq-card">
								<AqEmpty tone="danger" :title="$t('aqua.ui.zpm.not_found')" :text="$t('aqua.ui.zpm.not_found_text')">
									<router-link :to="{ path: listPath, query: pageQuery }" class="aq-btn aq-btn--primary">
										{{ $t('aqua.ui.zpm.to_list') }}
									</router-link>
								</AqEmpty>
							</div>

							<div v-else class="aq-layout">
								<form id="aq-zpm-form" class="aq-layout__main" method="POST" novalidate @submit.prevent="onSubmit">
									<div v-if="archived_at" class="aq-alert">
										<svg class="aq-alert__icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
											<path d="M4 7h16v12H4zM4 7l2-3h12l2 3M9 12h6" />
										</svg>
										<div>
											<strong>{{ $t('zpm.archived_at') }}: {{ archived_at }}</strong>
											<p class="aq-alert__text aq-alert__text--muted">{{ $t('aqua.ui.zpm.archived_hint') }}</p>
										</div>
									</div>
									<div v-if="isNotEditable && status_reason" class="aq-alert aq-alert--warning">
										<svg class="aq-alert__icon aq-alert__icon--warning" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
											<path d="M4 5h16v11H8l-4 4z" />
										</svg>
										<div>
											<strong>{{ $t('zpm.status_reason') }}</strong>
											<p class="aq-alert__text">{{ status_reason }}</p>
											<p v-if="status_changed_at" class="aq-alert__text aq-alert__text--muted">{{ status_changed_at }}</p>
										</div>
									</div>
									<div v-if="isNotEditable && canManage && isTransferred && !archived_at" class="aq-alert">
										<svg class="aq-alert__icon aq-alert__icon--info" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
											<rect x="5" y="10" width="14" height="10" rx="2" />
											<path d="M8 10V7a4 4 0 018 0v3" />
										</svg>
										<p class="aq-alert__text aq-alert__text--flat">{{ $t('zpm.locked_hint') }}</p>
									</div>

									<section class="aq-card">
										<div class="aq-card__head">
											<span class="aq-step">1</span>
											<div>
												<h2 class="aq-card__title">{{ $t('aqua.ui.zpm.section_subject') }}</h2>
												<p class="aq-card__subtitle">{{ $t('aqua.ui.zpm.section_subject_hint') }}</p>
											</div>
										</div>
										<div class="aq-card__body aq-grid">
											<AqField
												v-if="!isSubject"
												:label="$t('headers.iin_bin')"
												:required="!isNotEditable"
												:invalid="!!fieldErrors('bin')"
												:error="fieldErrors('bin') ? fieldErrors('bin').join(' ') : ''"
											>
												<div v-if="isNotEditable" class="aq-value aq-value--mono">{{ bin || '—' }}</div>
												<TheMask
													v-else
													v-model="bin"
													:masked="true"
													:name="'bin'"
													:placeholder="$t('filters.enter_iin')"
													:mask="['### ### ### ###']"
													type="text"
													inputmode="numeric"
													class="aq-input"
												/>
											</AqField>
											<AqField
												:label="$t('zpm.title')"
												:required="!isNotEditable"
												:wide="isSubject"
												:invalid="!!fieldErrors('title')"
												:error="fieldErrors('title') ? fieldErrors('title').join(' ') : ''"
											>
												<div v-if="isNotEditable" class="aq-value">{{ title || '—' }}</div>
												<input v-else v-model="title" type="text" maxlength="255" class="aq-input" />
											</AqField>
											<AqField v-if="!isNotEditable || code" :label="$t('zpm.code')" :hint="isNotEditable ? '' : $t('zpm.code_hint')">
												<div v-if="isNotEditable" class="aq-value">{{ code }}</div>
												<input v-else v-model="code" type="text" maxlength="255" class="aq-input" />
											</AqField>
											<AqField v-if="!isNotEditable || registration_date" :label="$t('zpm.registration_date')">
												<div v-if="isNotEditable" class="aq-value">{{ formatDay(registration_date) }}</div>
												<input v-else v-model="registration_date" type="date" class="aq-input" />
											</AqField>
											<AqField v-if="!isNotEditable || activity_type" :label="$t('zpm.activity_type')">
												<div v-if="isNotEditable" class="aq-value">{{ activity_type }}</div>
												<input v-else v-model="activity_type" type="text" maxlength="255" class="aq-input" />
											</AqField>
											<AqField v-if="!isNotEditable || address" :label="$t('zpm.address')" wide>
												<div v-if="isNotEditable" class="aq-value">{{ address }}</div>
												<input v-else v-model="address" type="text" maxlength="255" class="aq-input" />
											</AqField>
										</div>
									</section>

									<section class="aq-card">
										<div class="aq-card__head">
											<span class="aq-step">2</span>
											<div>
												<h2 class="aq-card__title">{{ $t('aqua.ui.zpm.section_registry') }}</h2>
												<p class="aq-card__subtitle">{{ $t('aqua.ui.zpm.section_registry_hint') }}</p>
											</div>
										</div>
										<div class="aq-card__body aq-grid">
											<AqField :label="$t('headers.start_date')" :required="!isNotEditable" :invalid="!!fieldErrors('start_date')">
												<div v-if="isNotEditable" class="aq-value">{{ formatDay(start_date) }}</div>
												<input v-else v-model="start_date" type="date" class="aq-input" />
											</AqField>
											<AqField
												:label="$t('headers.end_date')"
												:required="!isNotEditable"
												:invalid="!!fieldErrors('end_date')"
												:error="fieldErrors('end_date') ? fieldErrors('end_date').join(' ') : ''"
											>
												<div v-if="isNotEditable" class="aq-value">{{ formatDay(end_date) }}</div>
												<input v-else v-model="end_date" type="date" class="aq-input" :min="start_date || null" />
											</AqField>
											<AqField :label="$t('zpm.status')">
												<AquaSelect
													v-if="!isNotEditable && !isEdit"
													:value="status"
													:options="statusSelectOptions"
													@change="setStatus"
												/>
												<div v-else class="aq-value"><AqStatus :value="status" :label="statusLabel(status)" /></div>
											</AqField>
											<template v-if="!isSubject">
												<AqField :label="$t('zpm.activity_type_id')">
													<div v-if="isNotEditable && !canEditRegistry" class="aq-value">{{ activityTypeTitle }}</div>
													<AquaSelect
														v-else
														:value="activity_type_id"
														:options="activitySelectOptions"
														@change="setActivityType"
													/>
												</AqField>
												<AqField :label="$t('inputs.region')">
													<div v-if="isNotEditable && !canEditRegistry" class="aq-value">{{ region_title || '—' }}</div>
													<region
														v-else
														class="aq-legacy"
														:modelValue="region_id"
														:confinesValue="regionalConfines"
														:placeholder="$t('placeholder.choose')"
														@update:modelValue="region_id = $event"
													></region>
												</AqField>
												<div v-if="canEditRegistry" class="aq-registry-save">
													<button type="button" class="aq-btn" :disabled="busy" @click="saveRegistry">
														<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
															<path d="M5 12.5l4.5 4.5L19 7" />
														</svg>
														{{ $t('zpm.save_registry') }}
													</button>
												</div>
											</template>
										</div>
									</section>

									<section class="aq-card">
										<div class="aq-card__head">
											<span class="aq-step">3</span>
											<div>
												<h2 class="aq-card__title">{{ $t('headers.zpm_capacity') }}</h2>
												<p class="aq-card__subtitle">
													{{ isNotEditable ? $t('aqua.ui.zpm.capacity_view_hint') : $t('aqua.ui.zpm.capacity_edit_hint') }}
												</p>
											</div>
										</div>
										<div class="aq-card__body">
											<div v-if="isNotEditable" class="aq-capacities">
												<article v-for="fish in fishes" :key="fish.id" class="aq-capacity">
													<header class="aq-capacity__head">
														<strong>{{ fishName(fish) }}</strong>
														<span class="aq-capacity__pct">{{ $t('aqua.ui.zpm.left_pct', { value: percentOf(fish.remainder, fish.design_capacity) }) }}</span>
													</header>
													<div class="aq-capacity__numbers">
														<span>
															<small>{{ $t('headers.remainder') }}</small>
															<strong>{{ formatNum(fish.remainder) }}</strong>
														</span>
														<span>
															<small>{{ $t('aqua.ui.zpm.capacity_short') }}</small>
															<strong>{{ formatNum(fish.design_capacity) }}</strong>
														</span>
													</div>
													<span
														class="aq-bar"
														:class="{ 'aq-bar--low': percentOf(fish.remainder, fish.design_capacity) < 20 }"
														role="progressbar"
														:aria-valuenow="percentOf(fish.remainder, fish.design_capacity)"
														aria-valuemin="0"
														aria-valuemax="100"
													>
														<span :style="{ width: Math.min(percentOf(fish.remainder, fish.design_capacity), 100) + '%' }"></span>
													</span>
													<span class="aq-capacity__used">
														{{ $t('aqua.ui.zpm.used', { value: formatNum((Number(fish.design_capacity) || 0) - (Number(fish.remainder) || 0)) }) }}
													</span>
												</article>
												<p v-if="!fishes.length" class="aq-muted-note">{{ $t('aqua.ui.zpm.no_fishes') }}</p>
											</div>

											<template v-else>
												<div class="aq-fish-rows">
													<div class="aq-fish-rows__head" aria-hidden="true">
														<span>{{ $t('aqua.ui.zpm.fish') }}</span>
														<span>{{ $t('headers.zpm_capacity') }}</span>
														<span></span>
													</div>
													<div v-for="(fish, fishIndex) in fishes" :key="fish.id" class="aq-fish-row">
														<Fish
															class="aq-legacy"
															:required="true"
															:multiple="false"
															name="fish"
															:modelValue="fish.fish_id"
															:placeholder="$t('filters.select_fish')"
															v-on:update:modelValue="fish.fish_id = $event"
														/>
														<div class="aq-unit-input">
															<input
																v-model="fish.design_capacity"
																type="number"
																step=".01"
																min="0"
																inputmode="decimal"
																class="aq-input"
																:aria-label="$t('headers.zpm_capacity')"
															/>
															<span>{{ $t('headers.t_year') }}</span>
														</div>
														<button
															type="button"
															class="aq-btn aq-btn--danger aq-btn--icon"
															:disabled="fishes.length < 2"
															:aria-label="$t('tooltip.remove')"
															:title="$t('tooltip.remove')"
															@click="removeFish(fishIndex)"
														>
															<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
																<path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" />
															</svg>
														</button>
													</div>
												</div>
												<button type="button" class="aq-add" @click.stop.prevent="addFishes()">
													<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
														<path d="M12 5v14M5 12h14" />
													</svg>
													{{ $t('aqua.ui.zpm.add_fish') }}
												</button>
											</template>
										</div>
									</section>

									<section v-if="isNotEditable && history.length" class="aq-card">
										<div class="aq-card__head">
											<span class="aq-step">4</span>
											<div>
												<h2 class="aq-card__title">{{ $t('zpm.history') }}</h2>
												<p class="aq-card__subtitle">{{ $t('aqua.ui.zpm.history_count', { count: history.length }) }}</p>
											</div>
										</div>
										<ol class="aq-history">
											<li v-for="item in history" :key="item.id" class="aq-history__item">
												<span class="aq-history__dot" :class="`aq-history__dot--${historyTone(item.action)}`" aria-hidden="true"></span>
												<div class="aq-history__body">
													<div class="aq-history__top">
														<AqStatus :tone="historyTone(item.action)" :label="$t(`zpm.history_actions.${item.action}`)" />
														<span class="aq-history__meta">{{ item.created_at }}<template v-if="item.user"> · {{ item.user }}</template></span>
													</div>
													<p v-if="historyChanges(item)" class="aq-history__changes">{{ historyChanges(item) }}</p>
													<p v-if="item.comment" class="aq-history__comment">{{ item.comment }}</p>
												</div>
											</li>
										</ol>
									</section>
								</form>

								<aside class="aq-layout__side">
									<div class="aq-card aq-panel">
										<template v-if="isNotEditable">
											<AqKpi
												:label="$t('aqua.ui.zpm.remainder_total')"
												:value="formatNum(total_remainder)"
												:unit="$t('aqua.ui.zpm.unit')"
												:progress="remainderPercent"
												:tone="remainderPercent < 20 ? 'warning' : 'success'"
												:hint="$t('aqua.ui.zpm.of_capacity', { value: formatNum(total_capacity) })"
											/>
											<dl class="aq-panel__facts">
												<div>
													<dt>{{ $t('zpm.period') }}</dt>
													<dd>{{ formatDay(start_date) }} — {{ formatDay(end_date) }}</dd>
												</div>
												<div v-if="!isSubject">
													<dt>{{ $t('zpm.transferred_at') }}</dt>
													<dd>{{ isTransferred ? formatDateTime(transferred_at) : $t('zpm.not_transferred') }}</dd>
												</div>
											</dl>
										</template>

										<div v-if="!isNotEditable" class="aq-panel__actions">
											<button type="submit" form="aq-zpm-form" class="aq-btn aq-btn--primary aq-btn--block" :disabled="busy">
												{{ $t('button.save') }}
											</button>
											<router-link
												:to="{ path: $route.params.id ? listPath + $route.params.id : listPath, query: pageQuery }"
												class="aq-btn aq-btn--block"
											>
												{{ $t('button.cancel') }}
											</router-link>
										</div>

										<div v-else-if="canManage" class="aq-panel__actions">
											<span class="aq-panel__label">{{ $t('aqua.ui.zpm.manage') }}</span>
											<button v-if="archived_at" type="button" class="aq-btn aq-btn--primary aq-btn--block" :disabled="busy" @click="restoreZpm">
												{{ $t('zpm.restore') }}
											</button>
											<template v-else>
												<button
													v-if="!isTransferred"
													type="button"
													class="aq-btn aq-btn--primary aq-btn--block"
													:disabled="busy"
													@click="$modal.show('modal_transfer')"
												>
													<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
														<path d="M4 12h12M12 6l6 6-6 6M20 5v14" />
													</svg>
													{{ $t('zpm.transfer') }}
												</button>
												<button
													v-for="target in statusActions"
													:key="target"
													type="button"
													class="aq-btn aq-btn--block"
													:class="{ 'aq-btn--warn': target !== 'active' }"
													:disabled="busy"
													@click="openStatusModal(target)"
												>
													{{ $t(`zpm.status_action.${target}`) }}
												</button>
												<button type="button" class="aq-btn aq-btn--danger aq-btn--block" :disabled="busy" @click="$modal.show('modal_delete')">
													<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
														<path d="M4 7h16v12H4zM4 7l2-3h12l2 3M9 12h6" />
													</svg>
													{{ $t('zpm.archive') }}
												</button>
											</template>
										</div>
									</div>
								</aside>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<modal class="modal__block modal__block--middle" name="modal_transfer" :width="560" :min-width="320" :adaptive="true" height="auto">
			<div class="aq-page aq-modal">
				<span class="aq-modal__icon" aria-hidden="true">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<path d="M4 12h12M12 6l6 6-6 6M20 5v14" />
					</svg>
				</span>
				<h2 class="aq-modal__title">{{ $t('zpm.transfer') }}</h2>
				<p class="aq-modal__text">{{ $t('zpm.transfer_confirm', [zpm_nikad]) }}</p>
				<div class="aq-modal__actions">
					<button type="button" class="aq-btn" @click="$modal.hide('modal_transfer')">{{ $t('button.cancel') }}</button>
					<button type="button" class="aq-btn aq-btn--primary" :disabled="busy" @click="transferZpm">{{ $t('button.confirm') }}</button>
				</div>
			</div>
		</modal>

		<modal class="modal__block modal__block--middle" name="modal_status" :width="560" :min-width="320" :adaptive="true" height="auto">
			<div class="aq-page aq-modal">
				<h2 v-if="statusTarget" class="aq-modal__title">{{ $t(`zpm.status_action.${statusTarget}`) }}</h2>
				<p class="aq-modal__text">
					<span class="aq-code">{{ zpm_nikad }}</span>
					<template v-if="statusTarget"> → <AqStatus :value="statusTarget" :label="statusLabel(statusTarget)" /></template>
				</p>
				<label v-if="statusTarget !== 'active'" class="aq-modal__field">
					<span class="aq-modal__label">{{ $t('zpm.status_reason') }}</span>
					<textarea v-model="statusReason" class="aq-input aq-input--area" rows="4" maxlength="1000"></textarea>
					<span class="aq-modal__counter" :class="{ 'is-short': statusReason.trim().length < 5 }">
						{{ $t('aqua.ui.grown.reject_counter', { count: statusReason.trim().length }) }}
					</span>
				</label>
				<div class="aq-modal__actions">
					<button type="button" class="aq-btn" @click="$modal.hide('modal_status')">{{ $t('button.cancel') }}</button>
					<button
						type="button"
						class="aq-btn aq-btn--primary"
						:disabled="busy || (statusTarget !== 'active' && statusReason.trim().length < 5)"
						@click="changeStatus"
					>
						{{ $t('button.confirm') }}
					</button>
				</div>
			</div>
		</modal>

		<modal class="modal__block modal__block--middle" name="modal_delete" :width="560" :min-width="320" :adaptive="true" height="auto">
			<div class="aq-page aq-modal">
				<span class="aq-modal__icon aq-modal__icon--danger" aria-hidden="true">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<path d="M4 7h16v12H4zM4 7l2-3h12l2 3M9 12h6" />
					</svg>
				</span>
				<h2 class="aq-modal__title">{{ $t('zpm.archive') }}</h2>
				<p class="aq-modal__text">{{ $t('zpm.archive_confirm', [zpm_nikad]) }}</p>
				<label class="aq-modal__field">
					<span class="aq-modal__label">{{ $t('zpm.archive_comment') }}</span>
					<textarea v-model="archiveComment" class="aq-input aq-input--area" rows="3" maxlength="1000"></textarea>
				</label>
				<div class="aq-modal__actions">
					<button type="button" class="aq-btn" @click="$modal.hide('modal_delete')">{{ $t('button.cancel') }}</button>
					<button type="button" class="aq-btn aq-btn--danger-solid" :disabled="busy" @click="deleteZpm">{{ $t('zpm.archive') }}</button>
				</div>
			</div>
		</modal>

		<modal class="modal__block modal__block--middle" name="modal_success" :width="480" :min-width="320" :adaptive="true" height="auto">
			<div class="aq-page aq-modal aq-modal--center">
				<span class="aq-modal__icon aq-modal__icon--success" aria-hidden="true">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4">
						<path d="M5 12.5l4.5 4.5L19 7" />
					</svg>
				</span>
				<h2 class="aq-modal__title">{{ successText || $t('zpm.success') }}</h2>
				<div class="aq-modal__actions">
					<button type="button" class="aq-btn aq-btn--primary" @click="$modal.hide('modal_success')">{{ $t('button.close') }}</button>
				</div>
			</div>
		</modal>

		<modal class="modal__block modal__block--middle" name="modal_error" :width="560" :min-width="320" :adaptive="true" height="auto" @closed="clearError">
			<div class="aq-page aq-modal">
				<span class="aq-modal__icon aq-modal__icon--danger" aria-hidden="true">
					<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
						<path d="M12 7v6M12 16.5v.5" />
						<circle cx="12" cy="12" r="9" />
					</svg>
				</span>
				<h2 class="aq-modal__title">{{ $t('zpm.error') }}</h2>
				<p v-if="errorMessage.text" class="aq-modal__text" role="alert">{{ errorMessage.text }}</p>
				<div class="aq-modal__actions">
					<button type="button" class="aq-btn aq-btn--primary" @click="$modal.hide('modal_error')">{{ $t('button.close') }}</button>
				</div>
			</div>
		</modal>
	</div>
</template>

<style scoped>
.aq-shell {
	background: var(--aq-bg);
}
.aq-code {
	display: inline-flex;
	align-items: center;
	padding: 4px 10px;
	border: 1px solid var(--aq-border-strong);
	border-radius: 8px;
	background: var(--aq-surface);
	color: var(--aq-ink);
	font-family: ui-monospace, 'SFMono-Regular', Consolas, monospace;
	font-size: 14px;
	font-weight: 600;
	letter-spacing: 0.02em;
}
.aq-layout {
	display: grid;
	grid-template-columns: minmax(0, 1fr) 300px;
	align-items: start;
	gap: 24px;
}
.aq-layout__side {
	position: sticky;
	top: 16px;
}
.aq-alert__icon {
	flex-shrink: 0;
	margin-top: 1px;
	color: var(--aq-muted);
}
.aq-alert__icon--warning {
	color: var(--aq-warning);
}
.aq-alert__icon--info {
	color: var(--aq-primary);
}
.aq-alert__text {
	margin: 4px 0 0;
	line-height: 1.5;
}
.aq-alert__text--flat {
	margin: 0;
}
.aq-alert__text--muted {
	color: var(--aq-muted);
	font-size: 13px;
}
.aq-step {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 32px;
	height: 32px;
	border-radius: 10px;
	background: var(--aq-primary-soft);
	color: var(--aq-primary);
	font-weight: 700;
}
.aq-value {
	min-height: 44px;
	padding: 11px 0;
	border-bottom: 1px dashed var(--aq-border-strong);
	color: var(--aq-ink);
	font-size: 15px;
	font-weight: 500;
	word-break: break-word;
}
.aq-value--mono {
	font-family: ui-monospace, 'SFMono-Regular', Consolas, monospace;
	letter-spacing: 0.02em;
}
.aq-value ::v-deep .aq-badge {
	vertical-align: middle;
}
.aq-legacy ::v-deep .multiselect__tags {
	min-height: 44px;
	padding-top: 10px;
	border: 1px solid var(--aq-border-strong) !important;
	border-radius: 10px;
	transition: border-color 0.2s var(--aq-ease);
}
.aq-legacy ::v-deep .multiselect__tags:hover,
.aq-legacy ::v-deep .multiselect--active .multiselect__tags {
	border-color: var(--aq-primary) !important;
}
.aq-legacy ::v-deep .multiselect__single,
.aq-legacy ::v-deep .multiselect__placeholder {
	font-size: 15px;
}
.aq-legacy ::v-deep .input-wrapper,
.aq-legacy ::v-deep .input-body {
	margin: 0;
}
.aq-card__body ::v-deep .aqua-select {
	width: 100%;
}
.aq-card__body ::v-deep .aqua-select__control {
	height: 44px;
	border-color: var(--aq-border-strong);
	border-radius: 10px;
}
.aq-card__body ::v-deep .aqua-select__control:hover {
	border-color: var(--aq-primary);
}
.aq-registry-save {
	display: flex;
	justify-content: flex-end;
	grid-column: 1 / -1;
}
.aq-muted-note {
	margin: 0;
	color: var(--aq-muted);
	font-size: 14px;
}
.aq-capacities {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
	gap: 14px;
}
.aq-capacity {
	display: flex;
	flex-direction: column;
	gap: 10px;
	padding: 16px;
	border: 1px solid var(--aq-border);
	border-radius: var(--aq-radius);
	background: #fbfdff;
}
.aq-capacity__head {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 10px;
	font-size: 15px;
	line-height: 1.35;
}
.aq-capacity__pct {
	flex-shrink: 0;
	padding: 2px 8px;
	border-radius: 999px;
	background: var(--aq-success-soft);
	color: var(--aq-success);
	font-size: 13px;
	font-weight: 600;
}
.aq-capacity__numbers {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px;
}
.aq-capacity__numbers span {
	display: flex;
	flex-direction: column;
}
.aq-capacity__numbers small {
	color: var(--aq-muted);
	font-size: 12px;
}
.aq-capacity__numbers strong {
	color: var(--aq-ink);
	font-size: 22px;
	line-height: 1.2;
}
.aq-capacity__used {
	color: var(--aq-muted);
	font-size: 13px;
}
.aq-bar {
	display: block;
	height: 8px;
	border-radius: 999px;
	background: #e6edf6;
	overflow: hidden;
}
.aq-bar span {
	display: block;
	height: 100%;
	border-radius: inherit;
	background: var(--aq-success);
	transition: width 0.3s var(--aq-ease);
}
.aq-bar--low span {
	background: #e39a1b;
}
.aq-fish-rows {
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin-bottom: 14px;
}
.aq-fish-rows__head,
.aq-fish-row {
	display: grid;
	grid-template-columns: minmax(0, 1fr) 220px 44px;
	align-items: center;
	gap: 12px;
}
.aq-fish-rows__head {
	color: var(--aq-ink-2);
	font-size: 13px;
	font-weight: 500;
}
.aq-unit-input {
	position: relative;
}
.aq-unit-input .aq-input {
	padding-right: 64px;
}
.aq-unit-input span {
	position: absolute;
	top: 50%;
	right: 14px;
	color: var(--aq-muted);
	font-size: 14px;
	pointer-events: none;
	transform: translateY(-50%);
	white-space: nowrap;
}
.aq-add {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	width: 100%;
	min-height: 52px;
	border: 1.5px dashed var(--aq-border-strong);
	border-radius: var(--aq-radius);
	color: var(--aq-primary);
	font-weight: 600;
	transition: background 0.2s var(--aq-ease), border-color 0.2s var(--aq-ease);
}
.aq-add:hover {
	border-color: var(--aq-primary);
	background: var(--aq-primary-soft);
}
.aq-add:focus-visible {
	outline: none;
	box-shadow: 0 0 0 3px rgba(21, 127, 238, 0.25);
}
.aq-btn--icon {
	width: 44px;
	padding: 0;
}
.aq-btn--warn {
	border-color: #f0c98a;
	color: var(--aq-warning);
}
.aq-btn--warn:hover {
	border-color: var(--aq-warning);
	background: var(--aq-warning-soft);
	color: var(--aq-warning);
}
.aq-btn--danger-solid {
	border-color: var(--aq-danger);
	background: var(--aq-danger);
	color: #fff;
}
.aq-btn--danger-solid:hover {
	border-color: #b9271d;
	background: #b9271d;
	color: #fff;
}
.aq-history {
	margin: 0;
	padding: 8px 24px 16px;
	list-style: none;
}
.aq-history__item {
	position: relative;
	display: flex;
	gap: 14px;
	padding: 12px 0;
}
.aq-history__item:not(:last-child)::before {
	content: '';
	position: absolute;
	top: 30px;
	bottom: -12px;
	left: 5px;
	width: 2px;
	background: var(--aq-border);
}
.aq-history__dot {
	position: relative;
	flex-shrink: 0;
	width: 12px;
	height: 12px;
	margin-top: 8px;
	border-radius: 50%;
	background: var(--aq-border-strong);
	box-shadow: 0 0 0 3px var(--aq-surface);
}
.aq-history__dot--success {
	background: var(--aq-success);
}
.aq-history__dot--warning {
	background: #e39a1b;
}
.aq-history__dot--info {
	background: var(--aq-primary);
}
.aq-history__body {
	flex: 1;
	min-width: 0;
}
.aq-history__top {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 6px 12px;
}
.aq-history__meta {
	color: var(--aq-muted);
	font-size: 13px;
}
.aq-history__changes {
	margin: 6px 0 0;
	color: var(--aq-ink);
	font-size: 14px;
	line-height: 1.5;
}
.aq-history__comment {
	margin: 4px 0 0;
	color: var(--aq-ink-2);
	font-size: 14px;
	font-style: italic;
}
.aq-panel {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 20px;
}
.aq-panel ::v-deep .aq-kpi {
	padding: 14px 16px;
	box-shadow: none;
}
.aq-panel ::v-deep .aq-kpi__value {
	font-size: 26px;
}
.aq-panel__label {
	color: var(--aq-ink-2);
	font-size: 14px;
	font-weight: 600;
}
.aq-panel__facts {
	display: flex;
	flex-direction: column;
	gap: 10px;
	margin: 0;
}
.aq-panel__facts dt {
	color: var(--aq-muted);
	font-size: 12px;
	font-weight: 500;
}
.aq-panel__facts dd {
	margin: 2px 0 0;
	color: var(--aq-ink);
	font-size: 14px;
	font-weight: 600;
}
.aq-panel__actions {
	display: flex;
	flex-direction: column;
	gap: 10px;
}
.aq-panel__actions:not(:first-child) {
	padding-top: 14px;
	border-top: 1px solid var(--aq-border);
}
.aq-panel:empty {
	display: none;
}
.aq-modal {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 14px;
	padding: 28px;
	background: var(--aq-surface);
	text-align: left;
}
.aq-modal--center {
	align-items: center;
	text-align: center;
}
.aq-modal__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 48px;
	height: 48px;
	border-radius: 14px;
	background: var(--aq-primary-soft);
	color: var(--aq-primary);
}
.aq-modal__icon--danger {
	background: var(--aq-danger-soft);
	color: var(--aq-danger);
}
.aq-modal__icon--success {
	background: var(--aq-success-soft);
	color: var(--aq-success);
}
.aq-modal__title {
	margin: 0;
	font-size: 20px;
	font-weight: 700;
	line-height: 1.3;
	color: var(--aq-ink);
}
.aq-modal__text {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
	margin: 0;
	color: var(--aq-ink-2);
	font-size: 15px;
	line-height: 1.5;
}
.aq-modal__field {
	display: flex;
	flex-direction: column;
	gap: 6px;
	width: 100%;
	margin: 0;
}
.aq-modal__label {
	color: var(--aq-ink-2);
	font-size: 13px;
	font-weight: 500;
}
.aq-modal__counter {
	align-self: flex-end;
	color: var(--aq-muted);
	font-size: 12px;
}
.aq-modal__counter.is-short {
	color: var(--aq-danger);
}
.aq-input--area {
	min-height: 96px;
	resize: vertical;
	line-height: 1.5;
}
.aq-modal__actions {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	width: 100%;
	margin-top: 4px;
}
.aq-modal--center .aq-modal__actions {
	justify-content: center;
}
@media (max-width: 1280px) {
	.aq-layout {
		grid-template-columns: minmax(0, 1fr);
	}
	.aq-layout__side {
		position: static;
		order: -1;
	}
	.aq-panel {
		flex-flow: row wrap;
		align-items: flex-start;
	}
	.aq-panel > * {
		flex: 1 1 240px;
	}
	.aq-panel__actions {
		flex-basis: 100%;
		flex-direction: row;
		flex-wrap: wrap;
		align-items: center;
	}
	.aq-panel__actions .aq-panel__label {
		flex-basis: 100%;
	}
	.aq-panel__actions .aq-btn--block {
		width: auto;
		flex: 1 1 200px;
	}
}
@media (max-width: 768px) {
	.aq-card__body {
		padding: 16px;
	}
	.aq-fish-rows__head {
		display: none;
	}
	.aq-fish-row {
		grid-template-columns: minmax(0, 1fr) 44px;
	}
	.aq-fish-row .aq-legacy {
		grid-column: 1 / -1;
	}
}
</style>
